import React, {useEffect, useState, useCallback, useMemo} from "react";
import {Fancybox} from "@fancyapps/ui";
import {PhotoModel} from "../types/protocols";
import {useFirebaseImage, resolveFirebaseImageUrl} from "../Helpers/ImageLoader";
import {useUsersData} from "../Helpers/UsersContext";
import {buildPhotoCaption} from "../Helpers/photoCaptionUtils";
import {groupByYear, orderPhotos} from "../Helpers/photoOrdering";
import PhotoYearHeading from "./PhotoYearHeading";
import ClickableImage from "./ClickableImage";

// Individual photo component that loads its own URL and reports it back
const PhotoWithUrl: React.FC<{
    photo: PhotoModel,
    buildingKey: string,
    index: number,
    onImageClick: (photo: PhotoModel, url: string, index: number) => void,
    onUrlLoaded: (index: number, url: string) => void,
    size?: number,
    showDate?: boolean,
    highlighted?: boolean,
    showDeleteButton?: boolean,
    onDeleteClick?: (photo: PhotoModel) => void
}> = ({ photo, buildingKey, index, onImageClick, onUrlLoaded, size = 125, showDate, highlighted, showDeleteButton, onDeleteClick }) => {
    const { src } = useFirebaseImage(photo.storagePath);

    // Report the loaded URL back to parent
    useEffect(() => {
        if (src) {
            onUrlLoaded(index, src);
        }
    }, [src, index, onUrlLoaded]);

    return (
        <ClickableImage
            photo={photo}
            buildingKey={buildingKey}
            size={size}
            showDate={showDate !== undefined ? showDate : undefined}
            highlighted={highlighted}
            showDeleteButton={showDeleteButton}
            onDeleteClick={onDeleteClick}
            onImageClick={(clickedPhoto, url) => onImageClick(clickedPhoto, url, index)}
        />
    );
};

// Photo gallery component that manages all URLs and enables FancyBox navigation
const PhotoGallery: React.FC<{
    photos: { [key: string]: PhotoModel },
    buildingKey: string,
    onImageClick?: (photo: PhotoModel, url: string, index: number) => void,
    onFancyboxOpen?: () => void,
    onFancyboxClose?: () => void,
    thumbnailSize?: number,
    showDate?: boolean,
    /** Key of the photo to render as highlighted (split-pane selection). */
    selectedPhotoKey?: string,
    /** When true, shows the per-photo delete control (admin-gated by ClickableImage). */
    showDeleteButton?: boolean,
    onDeleteClick?: (photo: PhotoModel) => void
}> = ({ photos, buildingKey, onImageClick, onFancyboxOpen, onFancyboxClose, thumbnailSize = 125, showDate, selectedPhotoKey, showDeleteButton, onDeleteClick }) => {
    // Newest first, with year headings when the photos span more than one year.
    // `index` in `onImageClick` and the Fancybox order follow `orderPhotos`.
    const orderedPhotos = useMemo(() => orderPhotos(photos), [photos]);
    const groups = useMemo(() => groupByYear(orderedPhotos), [orderedPhotos]);
    const [photoUrls, setPhotoUrls] = useState<{ [index: number]: string }>({});
    const [allUrlsLoaded, setAllUrlsLoaded] = useState<boolean>(false);
    const { users } = useUsersData();
    
    const handleUrlLoaded = useCallback((index: number, url: string) => {
        setPhotoUrls(prev => ({ ...prev, [index]: url }));
    }, []);
    
    const handleImageClick = useCallback(async (clickedPhoto: PhotoModel, url: string, index: number) => {
        if (onImageClick) {
            onImageClick(clickedPhoto, url, index);
            return;
        }

        // Resolve every photo's real download URL before opening. Relying on the
        // thumbnails' loaded URLs races with FancyBox: if a thumbnail hasn't
        // finished loading, its slide would open on a placeholder that FancyBox
        // then caches (only fixed by reopening). Resolving here (cache-first)
        // guarantees real URLs regardless of thumbnail load state.
        const photoEntries = orderedPhotos;
        const resolvedUrls = await Promise.all(
            photoEntries.map((photo, photoIndex) =>
                resolveFirebaseImageUrl(photo.storagePath).catch(() => photoUrls[photoIndex] || photo.storagePath)
            )
        );
        const allPhotos = photoEntries.map((photo, photoIndex) => ({
            src: resolvedUrls[photoIndex],
            caption: buildPhotoCaption(photo, users),
        }));

        // Notify parent that Fancybox is opening
        if (onFancyboxOpen) {
            onFancyboxOpen();
        }

        Fancybox.show(
            allPhotos,
            {
                animated: true,
                showClass: "fancybox-fadeIn",
                startIndex: index,
                on: {
                    close: () => {
                        // Notify parent that Fancybox is closing
                        if (onFancyboxClose) {
                            onFancyboxClose();
                        }
                    }
                }
            }
        );
    }, [orderedPhotos, photoUrls, users, onImageClick, onFancyboxOpen, onFancyboxClose]);
    
    // Check if all URLs are loaded
    useEffect(() => {
        const photoCount = Object.keys(photos).length;
        const loadedCount = Object.keys(photoUrls).length;
        setAllUrlsLoaded(loadedCount === photoCount);
    }, [photoUrls, photos]);
    
    const renderPhoto = (photo: PhotoModel, index: number) => (
        <PhotoWithUrl
            key={photo.key}
            photo={photo}
            buildingKey={buildingKey}
            index={index}
            size={thumbnailSize}
            showDate={showDate}
            highlighted={selectedPhotoKey !== undefined && selectedPhotoKey === photo.key}
            showDeleteButton={showDeleteButton}
            onDeleteClick={onDeleteClick}
            onImageClick={handleImageClick}
            onUrlLoaded={handleUrlLoaded}
        />
    );

    if (groups.length === 1 && groups[0].year === null) {
        return (
            <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                {groups[0].items.map(renderPhoto)}
            </div>
        );
    }

    // Indexes run across the year groups so they match `orderedPhotos`.
    let offset = 0;
    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            {groups.map(group => {
                const start = offset;
                offset += group.items.length;
                return (
                    <div key={group.year ?? ''}>
                        <PhotoYearHeading year={group.year ?? ''} />
                        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                            {group.items.map((photo, i) => renderPhoto(photo, start + i))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PhotoGallery;
